import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { translations } from './translations';
import { api } from '../api/client';
import { LANGUAGES, LANGUAGE_KEYS, DEFAULT_ENABLED_LANGUAGES, FALLBACK_LANGUAGE } from './languages';

const LanguageContext = createContext(null);

function readStoredLanguage() {
  try {
    const stored = localStorage.getItem('exelflow_lang');
    return LANGUAGE_KEYS.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

// Rende utilizzabile qualunque risposta del server (anche incompleta o con
// lingue che questa versione dell'interfaccia non conosce).
function normalizeConfig(raw) {
  let enabled = LANGUAGE_KEYS.filter((k) => Array.isArray(raw?.enabled) && raw.enabled.includes(k));
  if (enabled.length === 0) enabled = [...DEFAULT_ENABLED_LANGUAGES];
  return { enabled, default: enabled.includes(raw?.default) ? raw.default : enabled[0] };
}

export function LanguageProvider({ children }) {
  // Lingua scelta dal visitatore su questo dispositivo (null = mai scelta)
  const [choice, setChoice] = useState(readStoredLanguage);
  // Lingue attive e predefinita, decise dal super-admin (null finché non arrivano)
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.getLanguageConfig()
      .then((res) => { if (mounted) setConfig(normalizeConfig(res?.data)); })
      // Se la configurazione non è raggiungibile si torna al comportamento storico
      .catch(() => { if (mounted) setConfig(normalizeConfig(null)); });
    return () => { mounted = false; };
  }, []);

  // Una lingua disattivata dall'admin non resta applicata: chi l'aveva scelta
  // torna alla predefinita. Finché la configurazione non arriva si rispetta la
  // scelta salvata, così non lampeggia un'altra lingua a ogni caricamento.
  const language = config
    ? (choice && config.enabled.includes(choice) ? choice : config.default)
    : (choice || FALLBACK_LANGUAGE);

  const availableLanguages = useMemo(
    () => (config ? LANGUAGES.filter((l) => config.enabled.includes(l.key)) : []),
    [config]
  );

  const setLanguage = useCallback((lang) => {
    if (!LANGUAGE_KEYS.includes(lang)) return;
    if (config && !config.enabled.includes(lang)) return;
    setChoice(lang);
    try {
      localStorage.setItem('exelflow_lang', lang);
    } catch {
      // storage non disponibile, la scelta resta valida solo per questa sessione
    }
  }, [config]);

  const t = useCallback(
    (key) => {
      const dict = translations[language] || translations.it;
      return dict[key] ?? translations.it[key] ?? key;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, availableLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage deve essere usato dentro a un LanguageProvider');
  return ctx;
}
